import React, { createContext, useContext, useState, useEffect } from "react";
import { Platform } from "../components/common";
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import { auth, db } from "../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { NotificationInitializer } from "../services/NotificationInitializer";

export type UserRole = "admin" | "manager" | "sales-representative" | "hand-lead";

interface User {
  id: string;
  username: string;
  role: UserRole;
  userGroupId: string;
  email: string;
  salesGroup?: string;
  firstName?: string;
  lastName?: string;
}

export interface AuthContextType {
  user: User | null;
  userRole: UserRole | null;
  loading: boolean;
  error: string | null;
  signIn: (usernameOrEmail: string, password: string, rememberMe?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = "mckenzie_auth_user";
const REMEMBER_ME_KEY = "mckenzie_remember_me";

// Helper functions for storage operations
const storage = {
  async getItem(key: string): Promise<string | null> {
    if (Platform.OS === "web") {
      return localStorage.getItem(key);
    }
    // For React Native, we'll use a simple in-memory storage for now
    return null;
  },
  async setItem(key: string, value: string): Promise<void> {
    if (Platform.OS === "web") {
      localStorage.setItem(key, value);
    }
    // For React Native, we'll skip storage for now
  },
  async removeItem(key: string): Promise<void> {
    if (Platform.OS === "web") {
      localStorage.removeItem(key);
    }
    // For React Native, we'll skip storage for now
  },
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Enhanced error handling for Firebase operations
 */
const handleFirebaseError = (error: any): string => {
  if (error.code === 'permission-denied') {
    return 'You do not have permission to perform this action';
  }
  if (error.code === 'unavailable') {
    return 'Service temporarily unavailable. Please try again';
  }
  if (error.code === 'resource-exhausted') {
    return 'Too many requests. Please try again later';
  }
  if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
    return 'Invalid email or password';
  }
  if (error.code === 'auth/invalid-email') {
    return 'Invalid email format';
  }
  if (error.code === 'auth/too-many-requests') {
    return 'Too many failed attempts. Please try again later';
  }
  if (error.code === 'auth/network-request-failed') {
    return 'Network error. Please check your connection';
  }
  return error.message || 'An unexpected error occurred. Please try again';
};

/**
 * Resolves a username or email to the actual Firebase email (case-insensitive)
 * @param usernameOrEmail - Username or email provided by user
 * @returns The actual Firebase email address
 */
const resolveUsernameOrEmailToEmail = async (usernameOrEmail: string): Promise<string> => {
  try {
    const normalized = usernameOrEmail.trim().toLowerCase();
    
    // Check if it looks like an email (contains @)
    if (normalized.includes('@')) {
      // Try to find user by email (case-insensitive)
      const usersRef = collection(db, "users");
      const emailQuery = query(usersRef, where("email", "==", normalized));
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        const userData = emailSnapshot.docs[0].data();
        return userData.email || normalized;
      }
      
      // Also try User_mail field for backward compatibility
      const altEmailQuery = query(usersRef, where("User_mail", "==", normalized));
      const altEmailSnapshot = await getDocs(altEmailQuery);
      
      if (!altEmailSnapshot.empty) {
        const userData = altEmailSnapshot.docs[0].data();
        return userData.email || userData.User_mail || normalized;
      }
      
      // If not found in Firestore but looks like an email, return it as-is
      // (Firebase auth will handle validation)
      return normalized;
    } else {
      // Personalized demo aliases (john.doe → shared demo accounts)
      const demoAliasSnap = await getDoc(doc(db, "demoLoginAliases", normalized));
      if (demoAliasSnap.exists()) {
        const aliasEmail = demoAliasSnap.data()?.email;
        if (aliasEmail) {
          return aliasEmail;
        }
      }

      // It's a username - look it up in Firestore (case-insensitive)
      const usersRef = collection(db, "users");
      
      // Try username field first
      const usernameQuery = query(usersRef, where("username", "==", normalized));
      const usernameSnapshot = await getDocs(usernameQuery);
      
      if (!usernameSnapshot.empty) {
        const userData = usernameSnapshot.docs[0].data();
        return userData.email || userData.User_mail || "";
      }
      
      // Try Username field (with capital U) for backward compatibility
      const altUsernameQuery = query(usersRef, where("Username", "==", normalized));
      const altUsernameSnapshot = await getDocs(altUsernameQuery);
      
      if (!altUsernameSnapshot.empty) {
        const userData = altUsernameSnapshot.docs[0].data();
        return userData.email || userData.User_mail || "";
      }
      
      // Try salesGroup field (for sales rep users like "07", "32F", etc.)
      const salesGroupQuery = query(usersRef, where("salesGroup", "==", normalized));
      const salesGroupSnapshot = await getDocs(salesGroupQuery);
      
      if (!salesGroupSnapshot.empty) {
        const userData = salesGroupSnapshot.docs[0].data();
        return userData.email || userData.User_mail || "";
      }
      
      // Also try uppercase salesGroup (in case it's stored as "32F" not "32f")
      const salesGroupQueryUpper = query(usersRef, where("salesGroup", "==", usernameOrEmail.trim().toUpperCase()));
      const salesGroupSnapshotUpper = await getDocs(salesGroupQueryUpper);
      
      if (!salesGroupSnapshotUpper.empty) {
        const userData = salesGroupSnapshotUpper.docs[0].data();
        return userData.email || userData.User_mail || "";
      }
      
      // Try with the original case (as last resort)
      const salesGroupQueryOriginal = query(usersRef, where("salesGroup", "==", usernameOrEmail.trim()));
      const salesGroupSnapshotOriginal = await getDocs(salesGroupQueryOriginal);
      
      if (!salesGroupSnapshotOriginal.empty) {
        const userData = salesGroupSnapshotOriginal.docs[0].data();
        return userData.email || userData.User_mail || "";
      }
      
      // Username not found
      throw new Error('Invalid username or email');
    }
  } catch (err) {
    console.error("Error resolving username/email:", err);
    throw new Error('Invalid username or email');
  }
};

// Optimized user fetching function with reduced queries and improved error handling
const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User> => {
  try {
    console.log("Fetching user data for:", firebaseUser.email);
    
    // First, try to get user directly by UID (most efficient)
    try {
      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        console.log("Found user by UID");
        const userData = userDocSnap.data();
        
        // Normalize role
        let normalizedRole: UserRole;
        const roleValue = 
          userData.role?.toLowerCase() || 
          userData.User_Role?.toLowerCase() || 
          "sales-representative"; // Default to sales-representative if no role found
        
        if (roleValue === "admin") {
          normalizedRole = "admin";
        } else if (roleValue === "manager" || roleValue === "sales-manager" || roleValue === "hand-lead") {
          normalizedRole = roleValue === "hand-lead" ? "hand-lead" : "manager";
        } else {
          normalizedRole = "sales-representative";
        }
        
        const user: User = {
          id: userDocSnap.id,
          username: userData.username || userData.Username || firebaseUser.email?.split('@')[0] || "",
          role: normalizedRole,
          userGroupId: userData.user_group_id || userData.salesGroup || "",
          email: userData.email || userData.User_mail || firebaseUser.email || "",
          salesGroup: userData.salesGroup || "",
          firstName: userData.firstName || undefined,
          lastName: userData.lastName || undefined
        };
        
        return user;
      }
    } catch (err) {
      console.log("Error fetching user by UID:", err);
      // Continue to other methods if this fails
    }
    
    // Try to find user by email
    const usersRef = collection(db, "users");
    const emailQuery = query(usersRef, where("email", "==", firebaseUser.email));
    let querySnapshot = await getDocs(emailQuery);

    // If not found by email, try alternative fields
    if (querySnapshot.empty) {
      console.log("User not found by email, trying alternative fields");
      
      // Try by username that matches email prefix
      if (firebaseUser.email) {
        const emailPrefix = firebaseUser.email.split('@')[0];
        const usernameQuery = query(usersRef, where("username", "==", emailPrefix));
        const usernameSnapshot = await getDocs(usernameQuery);
        
        if (!usernameSnapshot.empty) {
          querySnapshot = usernameSnapshot;
        }
      }
      
      // Try other fallback queries if still not found
      if (querySnapshot.empty) {
        const fallbackQueries = [
          query(usersRef, where("username", "==", firebaseUser.email)),
          query(usersRef, where("User_mail", "==", firebaseUser.email))
        ];
        
        for (const fallbackQuery of fallbackQueries) {
          const fallbackSnapshot = await getDocs(fallbackQuery);
          if (!fallbackSnapshot.empty) {
            querySnapshot = fallbackSnapshot;
            break;
          }
        }
      }
    }

    // If still not found, try to extract SalesGroup from email and find matching user
    if (querySnapshot.empty && firebaseUser.email) {
      console.log("User not found, trying to extract SalesGroup from email");
      const emailParts = firebaseUser.email.split('@');
      if (emailParts.length > 0 && emailParts[0]) {
        const possibleSalesGroup = emailParts[0];
        
        // Try to find by salesGroup
        const salesGroupQuery = query(usersRef, where("salesGroup", "==", possibleSalesGroup));
        const salesGroupSnapshot = await getDocs(salesGroupQuery);
        
        if (!salesGroupSnapshot.empty) {
          console.log("Found user by salesGroup");
          querySnapshot = salesGroupSnapshot;
        } else {
          // If still not found, create a new user document for this sales rep
          console.log("Creating new user for sales rep with group:", possibleSalesGroup);
          
          // Check if this is a sales rep email format (e.g., 22A@mckenzieportal.com)
          if (emailParts[1] === "mckenzieportal.com") {
            const newUserData = {
              email: firebaseUser.email,
              username: `${possibleSalesGroup}.mckenzie`,
              role: "sales-representative",
              salesGroup: possibleSalesGroup,
              uid: firebaseUser.uid,
              canCreateOrders: true,
              canEditProducts: false,
              canViewCustomers: true,
              canViewReports: true,
              lastLoginAt: new Date()
            };
            
            // Create the user document with the Firebase UID
            await setDoc(doc(db, "users", firebaseUser.uid), newUserData);
            
            // Return the newly created user
            return {
              id: firebaseUser.uid,
              username: `${possibleSalesGroup}.mckenzie`,
              role: "sales-representative",
              userGroupId: possibleSalesGroup,
              email: firebaseUser.email,
              salesGroup: possibleSalesGroup,
              firstName: undefined,
              lastName: undefined
            };
          }
        }
      }
    }

    if (querySnapshot.empty) {
      console.error("User not found in database after all attempts");
      throw new Error("User not found in database");
    }

    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();

    // Normalize role
    let normalizedRole: UserRole;
    const roleValue =
      userData.role?.toLowerCase() ||
      userData.User_Role?.toLowerCase() ||
      "sales-representative"; // Default to sales-representative

    console.log("User role from Firestore:", roleValue);
    
    if (roleValue === "admin") {
      normalizedRole = "admin";
    } else if (roleValue === "manager" || roleValue === "sales-manager" || roleValue === "hand-lead") {
      normalizedRole = roleValue === "hand-lead" ? "hand-lead" : "manager";
      console.log(`Normalized role set to: ${normalizedRole}`);
    } else {
      normalizedRole = "sales-representative";
      console.log("Normalized role set to: sales-representative");
    }

    const user: User = {
      id: userDoc.id,
      username: userData.username || userData.Username || firebaseUser.email?.split('@')[0] || "",
      role: normalizedRole,
      userGroupId: userData.user_group_id || userData.salesGroup || "",
      email: userData.email || userData.User_mail || firebaseUser.email || "",
      salesGroup: userData.salesGroup || "",
      firstName: userData.firstName || undefined,
      lastName: userData.lastName || undefined
    };

    // If the user document doesn't use the Firebase UID, update it for consistency
    if (userDoc.id !== firebaseUser.uid) {
      console.log("User document ID doesn't match Firebase UID, creating consistent document");
      await setDoc(doc(db, "users", firebaseUser.uid), {
        ...userData,
        uid: firebaseUser.uid,
        email: user.email,
        username: user.username,
        role: user.role,
        salesGroup: user.salesGroup || userData.salesGroup || ""
      });
    }

    return user;
  } catch (err) {
    console.error("Error fetching user data:", err);
    throw new Error("Failed to fetch user data");
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  console.log("AuthProvider component initializing");
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen for Firebase Auth state changes
  useEffect(() => {
    let isMounted = true;
    const timeout = setTimeout(() => {
      if (loading && isMounted) {
        console.log("Auth timeout - setting loading to false");
        setLoading(false);
      }
    }, 5000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return; // Prevent state updates after unmount
      
      try {
        console.log("Firebase Auth state changed:", firebaseUser ? firebaseUser.email : "signed out");
        
        if (firebaseUser) {
          console.log("Firebase user authenticated:", firebaseUser.email);
          
          // Fetch user data from Firestore
          const userData = await fetchUserData(firebaseUser);
          
          if (!isMounted) return; // Check again after async operation
          
          // Save user data to storage
          await storage.setItem(STORAGE_KEY, JSON.stringify(userData));
          
          setUser(userData);
          setUserRole(userData.role);
          
          // Initialize notifications for the authenticated user
          try {
            const notificationInitializer = NotificationInitializer.getInstance();
            await notificationInitializer.initialize(userData.id);
          } catch (error) {
            console.warn('Failed to initialize notifications:', error);
          }
        } else {
          console.log("Firebase user signed out");
          await storage.removeItem(STORAGE_KEY);
          setUser(null);
          setUserRole(null);
          
          // Reset notification initialization on logout
          try {
            const notificationInitializer = NotificationInitializer.getInstance();
            notificationInitializer.reset();
          } catch (error) {
            console.warn('Failed to reset notifications:', error);
          }
        }
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        console.error("Error handling auth state change:", err);
        const errorMessage = handleFirebaseError(err);
        setError(errorMessage);
      } finally {
        if (isMounted) {
          setLoading(false);
          clearTimeout(timeout);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [loading]);

  const signIn = async (usernameOrEmail: string, password: string, rememberMe = false) => {
  try {
      setLoading(true);
      setError(null);

      // Resolve username or email to actual Firebase email (case-insensitive)
      const actualEmail = await resolveUsernameOrEmailToEmail(usernameOrEmail);

      // Sign in with Firebase Auth using the resolved email
      const userCredential = await signInWithEmailAndPassword(auth, actualEmail, password);
      const firebaseUser = userCredential.user;

      // Fetch user data using the enhanced function
      const userData = await fetchUserData(firebaseUser);

      // Save remember me preference
      await storage.setItem(REMEMBER_ME_KEY, JSON.stringify(rememberMe));

      // If remember me is checked, save user data to storage
      // Otherwise, only keep it in memory
      if (rememberMe) {
        await storage.setItem(STORAGE_KEY, JSON.stringify(userData));
      }

      setUser(userData);
      setUserRole(userData.role);
    } catch (err: any) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      await storage.removeItem(STORAGE_KEY);
      await storage.removeItem(REMEMBER_ME_KEY);
      setUser(null);
      setUserRole(null);
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const value: AuthContextType = {
    user,
    userRole,
    loading,
    error,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};